import React from 'react';

import SportsBarIcon from '@mui/icons-material/SportsBar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowList from './components/WorkflowList';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  { label: 'ストーリー導入 + 雛形作成', title: 'プロジェクト雛形を作成する', active: true },
];

const HandsonStoryScaffold: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="導入編 — ストーリー導入 + プロジェクト雛形作成"
      seriesName="導入編"
      tags={['Hands-on', 'Spring Boot', 'Docker', 'First AI Generation']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: '環境構築',
        path: '/devdocs/handson/preparation',
      }}
      nextLink={{
        label: 'Steeringルールの書き方',
        path: '/devdocs/handson/steering-rules',
      }}
    >
      <WorkflowGoalBox
        prerequisites={[
          'Kiroがインストールされ、正常に起動している',
          'Java 21がインストールされている',
          'Dockerがインストールされ、docker コマンドが使える状態になっている',
          'Gitがインストールされ、GitHubアカウントが作成済みである',
          'ブラウザでSpring Initializrにアクセスできる',
        ]}
        completionCriteria={[
          'Spring Bootプロジェクトの雛形が作成されている',
          'docker-compose.ymlがKiroによって生成されている',
          'HELPME.mdがKiroによって生成されている',
          'HELPME.mdの手順に従い、MySQLコンテナが正常に起動する',
          'アプリケーションが起動する（Spring Securityのハマりポイントを解決済み）',
          'GitHubにリポジトリを作成し、初回コミット・プッシュが完了している',
        ]}
      >
        友人の相談をきっかけに、チームTODO管理アプリの開発プロジェクトを立ち上げる。Spring
        Bootの雛形を作成し、Kiroで初めてのAI生成（docker-compose.yml）を体験する。
      </WorkflowGoalBox>

      {/* 目次 */}
      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '導入ストーリー' },
          { id: 'section-2', label: 'これから作るもの' },
          { id: 'section-3', label: 'プロジェクト雛形を作成する' },
          { id: 'section-4', label: 'Kiroで初めてのAI生成 — docker-compose.yml' },
          { id: 'section-5', label: 'application設定を書く' },
          { id: 'section-6', label: 'アプリケーションを起動する — 最初のハマりポイント' },
          { id: 'section-7', label: '現在のプロジェクト構成' },
          { id: 'section-8', label: 'Gitリポジトリを作成してプッシュする' },
          { id: 'section-9', label: 'この体験で学んだこと' },
          { id: 'section-10', label: '次のステップに向けて' },
        ]}
      />

      {/* セクション1: 導入ストーリー */}
      <WorkflowSection title="導入ストーリー" id="section-1" number={1}>
        <Box
          sx={{
            border: 1,
            borderColor: '#e0d6b8',
            borderRadius: 2,
            p: 3,
            bgcolor: '#fdf8ec',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <SportsBarIcon sx={{ fontSize: 24, color: '#f9a825', mt: 0.5, flexShrink: 0 }} />
            <Typography
              variant="body2"
              sx={{
                lineHeight: 2.4,
                fontStyle: 'italic',
                whiteSpace: 'pre-line',
                fontSize: '0.9rem',
                color: '#3e2c1c',
              }}
            >
              {`金曜の夜、仕事終わりのビールを片手にスマホが鳴った。大学の同期、A。

「久しぶり。ちょっと相談があるんだけど」

Aは飲食チェーンに就職して3年目。最近、新店舗の立ち上げチームでリーダーを任されたらしい。

「正直やばくて。今スプレッドシートでタスク管理してるんだけど、もう無理。誰が何やってるか分からないし、期限過ぎてから『あれどうなった？』って聞いて初めて手つかずだって分かる。チャットで『終わりました』って報告もらっても他のメッセージに埋もれて見逃すし」

「PMに『来月の新店オープンまでに改善案持ってこい』って言われてて。ITで何とかならない？ シンプルでいいんだ。誰が何をいつまでにやるか、終わったかどうか、それだけ見えればいい」

——なるほど。やることは明確だ。

実は半年前、別の友人に頼まれて勢いでアプリを作ったことがある。要件を詰めずにいきなりコードを書き始めて、途中で「思ってたのと違う」と言われて作り直し。結局3倍の時間がかかった。

今回は同じ失敗はしない。まず要件をちゃんと整理する。その上で、最近気になっていたAI駆動開発で一気に仕上げる。普段仕事で使っているJavaとSpring Bootなら勝手も分かる。

「OK。来週末までに動くもの見せるよ」`}
            </Typography>
          </Stack>
        </Box>

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            ポイント
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            このストーリーがハンズオン全体の題材になります。Aの困りごとを「チームTODO管理機能」として要件定義し、AI駆動で実装するまでが今回のゴールです。
          </Typography>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション2: これから作るもの */}
      <WorkflowSection title="これから作るもの" id="section-2" number={2}>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            ['アプリ名', 'チームTODO管理（Team TODO）'],
            [
              '解決する課題',
              '誰が何をいつまでにやるか見えない / 期限超過に気づかない / 完了報告が埋もれる',
            ],
            ['技術スタック', 'Java 21 / Spring Boot 4.0.x / MySQL 8.0 / MyBatis / Gradle'],
            ['認証', 'なし（今回のハンズオンでは省略）'],
            ['メインパート', 'チームTODO管理機能（手順書あり・完成例あり）'],
            ['演習パート', '掲示板機能（チェックリストのみ・自力で実施）※後続記事'],
          ]}
        />

        <WorkflowInfoBox type="warning">
          <Typography variant="body2">
            <strong>注意</strong>:
            このハンズオンではログイン機能は設けません。認証・認可を省略することで、ビジネスロジックの設計とAI駆動開発のフローに集中できるようにしています。
          </Typography>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション3: プロジェクト雛形を作成する */}
      <WorkflowSection title="プロジェクト雛形を作成する" id="section-3" number={3}>
        <WorkflowSection title="Spring Initializrでプロジェクト生成" subNumber="3.1">
          <WorkflowText>
            ブラウザで{' '}
            <Link href="https://start.spring.io/" target="_blank" rel="noopener">
              Spring Initializr
            </Link>{' '}
            にアクセスし、以下の設定でプロジェクトを生成します。
          </WorkflowText>

          <WorkflowTable
            headers={['項目', '設定値']}
            rows={[
              ['Project', 'Gradle - Groovy'],
              ['Language', 'Java'],
              ['Spring Boot', '4.0.x（MyBatis対応版）'],
              ['Group', 'com.example'],
              ['Artifact', 'team-todo'],
              ['Package name', 'com.example.teamtodo'],
              ['Packaging', 'Jar'],
              ['Configuration', 'YAML'],
              ['Java', '21'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="依存ライブラリを追加する" subNumber="3.2">
          <WorkflowText>
            「ADD DEPENDENCIES」ボタンをクリックし、以下の6つを追加します。
          </WorkflowText>

          <WorkflowTable
            headers={['依存ライブラリ', '説明']}
            rows={[
              ['Spring Web', 'REST APIのエンドポイントを作成するために必要'],
              ['MyBatis Framework', 'SQLマッパー。DBアクセスに使用'],
              ['MySQL Driver', 'MySQLへの接続ドライバ'],
              ['Lombok', 'getter/setter等のボイラープレートコードを自動生成'],
              ['Validation', 'リクエストのバリデーション（@NotNull, @Size等）'],
              ['Spring Security', '認証・認可フレームワーク（今回は全許可設定で使用）'],
            ]}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>: MyBatis Frameworkは現時点でSpring Boot
              4.0.xにのみ対応しています。Spring Bootのバージョン選択で{' '}
              <strong>4.0系の最新パッチ</strong>{' '}
              を選んでください。4.1を選ぶとMyBatisが依存候補に表示されません。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowTipBox title="なぜ最初に全部入れるのか？">
            <WorkflowText>
              今回のハンズオンでは最終的に6つ全てを使うことが分かっています。使うことが確定しているなら、Spring
              Initializrがバージョン互換性を自動解決してくれるこのタイミングで入れるのが効率的です。
            </WorkflowText>
            <WorkflowText>
              なお、ソフトウェア開発にはYAGNI原則（You Ain&#39;t Gonna Need
              It）という考え方があります。「今必要ないものは作るな」という原則で、将来使うかもしれない機能、まだ要件にない拡張ポイント、念のためのユーティリティクラスなど——「いつか必要になるかも」で先回りして作ったものは、たいてい使われないまま保守コストだけ増やします。
            </WorkflowText>
            <WorkflowText>
              今回の依存追加は「全部使うと分かっている」ケースなのでYAGNI違反にはなりませんが、実務では「本当に今必要か？」を常に問いかけることで、コードベースをシンプルに保てます。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="ダウンロードと展開" subNumber="3.3">
          <WorkflowList
            variant="ordered"
            items={[
              '「GENERATE」ボタンをクリックしてZIPファイルをダウンロード',
              '任意のディレクトリに解凍する',
              'Kiroでそのディレクトリを開く（File > Open Folder）',
            ]}
          />

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              確認: Kiroで開いた後、以下のディレクトリ構造になっていればOKです。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowDirectoryTree
            root="team-todo/"
            entries={[
              { name: 'build.gradle' },
              { name: 'settings.gradle' },
              { name: 'gradlew' },
              { name: 'gradlew.bat' },
              {
                name: 'gradle',
                children: [{ name: 'wrapper', children: [] }],
              },
              {
                name: 'src',
                children: [
                  {
                    name: 'main',
                    children: [
                      {
                        name: 'java/com/example/teamtodo',
                        children: [{ name: 'TeamTodoApplication.java' }],
                      },
                      {
                        name: 'resources',
                        children: [
                          { name: 'application.yml' },
                          { name: 'static', children: [] },
                          { name: 'templates', children: [] },
                        ],
                      },
                    ],
                  },
                  {
                    name: 'test',
                    children: [
                      {
                        name: 'java/com/example/teamtodo',
                        children: [{ name: 'TeamTodoApplicationTests.java' }],
                      },
                    ],
                  },
                ],
              },
              { name: 'HELP.md' },
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: Kiroで初めてのAI生成 */}
      <WorkflowSection title="Kiroで初めてのAI生成 — docker-compose.yml" id="section-4" number={4}>
        <WorkflowText>
          ここからがAI駆動開発の第一歩です。ローカル開発用のMySQL環境を、Kiroに生成してもらいます。
        </WorkflowText>

        <WorkflowSection title="Kiroにお願いする" subNumber="4.1">
          <WorkflowText>Kiroのチャットに以下のプロンプトを入力します。</WorkflowText>

          <WorkflowChatBubble role="human">
            {`ローカル開発用のdocker-compose.ymlを作成してください。

要件:
- MySQL 8.0
  - ポート: 3306
  - DB名: team_todo
  - ユーザー: root
  - パスワード: root
  - 文字コード: utf8mb4
  - データの永続化（volume）
  - ヘルスチェック設定
- Flyway 7.15.0（DBマイグレーション用）
  - MySQLの起動完了を待ってから実行する（depends_on + healthcheck）
  - マイグレーションSQLの配置先: src/main/resources/db/migration
  - 接続先はMySQL側のDB名・ユーザー・パスワードと一致させる

追加で HELPME.md を作成してください:
- docker compose の起動・停止コマンド
- 動作確認方法（MySQLへの接続確認コマンド）
- Flywayに関する注意事項（マイグレーションファイルがない場合の挙動）
- よくあるトラブルと対処法`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              AIに依頼するときのコツは「何が欲しいか」と「具体的な要件」を箇条書きで渡すことです。曖昧な指示だとAIが推測で補完するため、意図と異なるものが出力されることがあります。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="生成結果を確認する" subNumber="4.2">
          <WorkflowText>
            Kiroがdocker-compose.ymlを生成します。以下のような内容になるはずです（AIの生成結果は毎回微妙に異なります）。
          </WorkflowText>

          <WorkflowCodeBlock
            title="docker-compose.yml（生成例）"
            language="yaml"
            collapsible
            code={`version: '3.8'

services:
  mysql:
    image: mysql:8.0
    container_name: team-todo-mysql
    command: --character-set-server=utf8mb4 --collation-server=utf8mb4_general_ci
    environment:
      MYSQL_ROOT_PASSWORD: root
      MYSQL_DATABASE: team_todo
    ports:
      - "3306:3306"
    volumes:
      - mysql-data:/var/lib/mysql
    healthcheck:
      test: ["CMD", "mysqladmin", "ping", "-h", "localhost", "-uroot", "-proot"]
      interval: 5s
      timeout: 5s
      retries: 10
      start_period: 30s

  flyway:
    image: flyway/flyway:7.15.0
    platform: linux/amd64
    container_name: team-todo-flyway
    depends_on:
      mysql:
        condition: service_healthy
    volumes:
      - ./src/main/resources/db/migration:/flyway/sql
    command: -url=jdbc:mysql://mysql:3306/team_todo -user=root -password=root migrate

volumes:
  mysql-data:`}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              生成されたファイルは必ず目を通してください。AIは意図通りに生成することが多いですが、以下の点を確認する習慣をつけましょう。
            </Typography>
            <WorkflowList
              items={[
                'ポート番号は指定通りか？',
                '環境変数（DB名・パスワード等）は指定通りか？',
                'volumeが設定されているか？（データ永続化）',
                'FlywayのDB接続情報がMySQLと一致しているか？',
                'depends_on + healthcheckでMySQLの起動完了を待つ構成になっているか？',
              ]}
            />
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="動作確認" subNumber="4.3">
          <WorkflowText>
            Kiroが生成した HELPME.md
            に動作確認方法が記載されています。その手順に従ってMySQLが正常に起動していることを確認してください。
          </WorkflowText>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              AIに成果物を生成させるときは、本体だけでなく「確認方法」や「注意事項」も一緒に出力させると便利です。自分で調べる時間を節約できますし、後から他の人が見ても使い方が分かります。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowTipBox title="うまくいかないときのAIへの聞き方">
            <WorkflowText>
              動作確認でエラーが出た場合、AIに以下の情報を渡すと解決が早くなります。
            </WorkflowText>
            <WorkflowTable
              headers={['渡す情報', '例']}
              rows={[
                ['何をしたか（実行したコマンド）', 'docker compose up -d を実行した'],
                ['何が起きたか（エラーメッセージ）', 'ターミナルに出たエラーをそのままコピペ'],
                ['何を期待していたか', 'MySQLに接続できるはずだった'],
                ['関連ファイル', '#docker-compose.yml で生成したファイルを渡す'],
              ]}
            />
            <WorkflowText>
              「動きません」だけだとAIも推測するしかありません。「やったこと」「起きたこと」「期待していたこと」の3点セットを伝えると、AIはピンポイントで原因を特定できます。
            </WorkflowText>
            <WorkflowText>
              ちなみにこれはAIに限った話ではありません。チームの先輩やリーダーに相談するときも全く同じです。3点セットを整理してから相談する癖をつけると、AI相手でも人間相手でも、問題解決のスピードが段違いに上がります。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: application設定を書く */}
      <WorkflowSection title="application設定を書く" id="section-5" number={5}>
        <WorkflowText>
          Spring BootからMySQLに接続するための設定を記述します。Spring
          InitializrでConfigurationsをYAMLに指定しているため、src/main/resources/application.yml
          が最初から存在しています。
        </WorkflowText>
        <WorkflowText>
          ここではあえて手動で書きます。Kiroが生成した docker-compose.yml
          を見て、接続に必要な値を自分で読み取り、application.ymlに設定してください。
        </WorkflowText>

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            やること
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            docker-compose.ymlから以下の対応関係を読み取り、application.ymlにDB接続設定を記述する。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowTable
          headers={['docker-compose.ymlの値', 'application.ymlの設定キー']}
          rows={[
            ['ポートマッピング（ホスト側）', 'spring.datasource.url のポート番号'],
            ['MYSQL_DATABASE', 'spring.datasource.url のDB名'],
            ['MYSQL_ROOT_PASSWORD', 'spring.datasource.password'],
            ['ユーザー（rootを使用）', 'spring.datasource.username'],
          ]}
        />

        <WorkflowTipBox title="ヒント: application.ymlの構造">
          <WorkflowText>
            Spring Bootのデータソース設定は以下の構造で書きます。{'{...}'}
            の部分をdocker-compose.ymlの値で埋めてください。
          </WorkflowText>
          <WorkflowCodeBlock
            language="yaml"
            code={`spring:
  datasource:
    url: jdbc:mysql://localhost:{ポート}/{DB名}?useSSL=false&allowPublicKeyRetrieval=true
    username: {ユーザー名}
    password: {パスワード}
    driver-class-name: com.mysql.cj.jdbc.Driver`}
          />
        </WorkflowTipBox>

        <WorkflowTipBox title="なぜこれはAIに任せないのか？">
          <WorkflowText>
            AIに「docker-compose.ymlに合わせてapplication.ymlを書いて」とお願いすれば一瞬で完成します。でも、この段階ではあえて手動で書いてもらいます。
          </WorkflowText>
          <WorkflowText>
            理由は「AIが生成したファイルの内容を自分で読み取って、別のファイルとの整合性を自分で確認する」練習だからです。AI駆動開発では、AIの出力を鵜呑みにせず「この値は本当に正しいか？」と照合する力が重要になります。その最初のトレーニングがこのステップです。
          </WorkflowText>
        </WorkflowTipBox>
      </WorkflowSection>

      {/* セクション6: アプリケーションを起動する — 最初のハマりポイント */}
      <WorkflowSection
        title="アプリケーションを起動する — 最初のハマりポイント"
        id="section-6"
        number={6}
      >
        <WorkflowSection title="起動して動作確認する" subNumber="6.1">
          <WorkflowText>ターミナルで以下のコマンドを実行します。</WorkflowText>
          <WorkflowCodeBlock language="bash" code="./gradlew bootRun" />
          <WorkflowText>
            起動したら、別のターミナルでcurlを実行してみましょう。-v
            オプションでレスポンスの詳細を確認します。
          </WorkflowText>
          <WorkflowCodeBlock language="bash" code="curl -v http://localhost:8080" />

          <WorkflowInfoBox type="success">
            <Typography variant="body2" fontWeight={600}>
              期待値
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              404 Not Found
              が返ってきたら成功です。まだエンドポイントを1つも作っていないので404は正常。アプリケーションが正しく起動し、リクエストを受け付けていることを意味します。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2" fontWeight={600}>
              401 Unauthorized が返ってきましたか？
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              大丈夫です。ここでつまずくのは想定通りです。焦らず読み進めてください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="何が起きているか" subNumber="6.2">
          <WorkflowText>
            Spring
            Securityを依存に含めると、デフォルトで全てのエンドポイントが認証必須になります。まだ認証の設定をしていないため、どのリクエストも弾かれています。
          </WorkflowText>
          <WorkflowText>
            このハンズオンではログイン機能は作りません。開発中は全リクエストを許可する設定が必要です。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="Kiroに調査・解決してもらう" subNumber="6.3">
          <WorkflowText>
            ここでAIの力を借ります。Kiroのチャットに以下のプロンプトを入力してください。
          </WorkflowText>

          <WorkflowChatBubble role="human">
            {`アプリケーションを起動してcurlでリクエストすると401 Unauthorizedが返ってきます。

Spring Securityの設定で全てのリクエストを許可するようにしたいです。
開発用のSecurityConfigクラスを作成してください。

条件:
- 全てのエンドポイントを認証なしでアクセス可能にする
- CSRF保護を無効化する
- パッケージは com.example.teamtodo.config に配置する`}
          </WorkflowChatBubble>
        </WorkflowSection>

        <WorkflowSection title="生成結果を確認する" subNumber="6.4">
          <WorkflowText>Kiroが以下のようなクラスを生成します。</WorkflowText>
          <WorkflowCodeBlock
            title="SecurityConfig.java（生成例）"
            language="java"
            collapsible
            code={`package com.example.teamtodo.config;

import org.springframework.context.annotation.Bean;
import org.springframework.context.annotation.Configuration;
import org.springframework.security.config.annotation.web.builders.HttpSecurity;
import org.springframework.security.web.SecurityFilterChain;

@Configuration
public class SecurityConfig {

    @Bean
    public SecurityFilterChain securityFilterChain(HttpSecurity http) throws Exception {
        http
            .csrf(csrf -> csrf.disable())
            .authorizeHttpRequests(auth -> auth
                .anyRequest().permitAll()
            );
        return http.build();
    }
}`}
          />
        </WorkflowSection>

        <WorkflowSection title="再起動して確認" subNumber="6.5">
          <WorkflowCodeBlock
            language="bash"
            code={`# 起動中のプロセスを Ctrl+C で停止してから
./gradlew bootRun`}
          />
          <WorkflowText>curlで確認します。</WorkflowText>
          <WorkflowCodeBlock language="bash" code="curl http://localhost:8080" />
          <WorkflowText>
            今度は404（Not
            Found）が返ってきます。まだエンドポイントを1つも作っていないので404は正常です。401ではなくなったことがポイントです。
          </WorkflowText>

          <WorkflowInfoBox type="success">
            <Typography variant="body2" fontWeight={600}>
              成功！ Spring Securityの問題を解決できました。これで開発を進める準備が整いました。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション7: 現在のプロジェクト構成 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-7" number={7}>
        <WorkflowText>ここまでの作業で、プロジェクトは以下の状態になっています。</WorkflowText>

        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml', desc: 'Kiroが生成', highlight: '#1976d2' },
            { name: 'HELPME.md', desc: 'Kiroが生成', highlight: '#1976d2' },
            {
              name: 'src',
              children: [
                {
                  name: 'main',
                  children: [
                    {
                      name: 'java/com/example/teamtodo',
                      children: [
                        { name: 'TeamTodoApplication.java' },
                        {
                          name: 'config',
                          children: [
                            {
                              name: 'SecurityConfig.java',
                              desc: 'Kiroが生成',
                              highlight: '#1976d2',
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: 'resources',
                      children: [
                        { name: 'application.yml', desc: '手動で追記', highlight: '#e65100' },
                      ],
                    },
                  ],
                },
                { name: 'test', children: [] },
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション8: Gitリポジトリを作成してプッシュする */}
      <WorkflowSection title="Gitリポジトリを作成してプッシュする" id="section-8" number={8}>
        <WorkflowSection title="GitHubでリポジトリを作成する" subNumber="8.1">
          <WorkflowList
            variant="ordered"
            items={[
              <Typography key="1" variant="body2" color="text.secondary">
                <Link href="https://github.com/" target="_blank" rel="noopener">
                  GitHub
                </Link>{' '}
                にログインする
              </Typography>,
              '右上の「+」→「New repository」をクリック',
              '以下の設定で作成する',
            ]}
          />

          <WorkflowTable
            headers={['項目', '設定値']}
            rows={[
              ['Repository name', 'team-todo'],
              ['Description', 'Team TODO management app (AI-driven development hands-on)'],
              ['Visibility', 'Private（推奨）'],
              ['Initialize', 'チェックなし（README, .gitignore, license 全て不要）'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="ローカルでGit初期化とコミット" subNumber="8.2">
          <WorkflowText>
            プロジェクトのルートディレクトリで以下のコマンドを実行します。
          </WorkflowText>
          <WorkflowCodeBlock
            language="bash"
            code={`git init
git add .
git commit -m "initial commit"`}
          />

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              .gitignore はSpring Initializrが自動生成してくれています。build/ ディレクトリや
              .gradle/ などのビルド成果物は既に除外対象になっているので、git add .
              で余計なファイルがコミットされる心配はありません。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="リモートに接続してプッシュ" subNumber="8.3">
          <WorkflowText>
            GitHubで作成したリポジトリのURLを使って、リモートを設定しプッシュします。
          </WorkflowText>
          <WorkflowCodeBlock
            language="bash"
            code={`git remote add origin https://github.com/{あなたのユーザー名}/team-todo.git
git branch -M main
git push -u origin main`}
          />
          <WorkflowText>
            GitHubのリポジトリページをリロードして、ファイルが表示されていれば完了です。
          </WorkflowText>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" color="text.secondary">
              <strong>補足</strong>:
              以降のステップでも区切りのいいタイミングでコミット・プッシュする習慣をつけましょう。作業の途中で壊してしまっても、直前のコミットに戻れる安心感があります。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション9: この体験で学んだこと */}
      <WorkflowSection title="この体験で学んだこと" id="section-9" number={9}>
        <WorkflowTable
          headers={['学び', '説明']}
          rows={[
            [
              'AIは「万能」ではなく「道具」',
              'docker-compose.ymlの生成は適切だったが、Spring Securityのデフォルト挙動までは面倒を見てくれない。エラーが出たら自分で気づいてAIに聞く必要がある',
            ],
            [
              'プロンプトの具体性が品質を決める',
              '「docker-compose作って」より「MySQL 8.0 / ポート3306 / DB名team_todo / ...」のように要件を箇条書きにした方が精度が上がる',
            ],
            [
              'AIの出力は必ず確認する',
              '生成されたファイルを盲信せず、設定値・構造・整合性を目視で確認する習慣が大切',
            ],
            [
              'エラーは怖くない',
              'エラーが出たらAIに状況を伝えて解決策を聞く。「何が起きているか」を正確に伝えることが鍵',
            ],
          ]}
        />
      </WorkflowSection>

      {/* セクション10: 次のステップに向けて */}
      <WorkflowSection title="次のステップに向けて" id="section-10" number={10}>
        <WorkflowText>
          プロジェクトの雛形は整いました。次の記事では、AI駆動開発の品質を左右する
          Steeringルール（AIへのルールファイル）の書き方を学びます。
        </WorkflowText>
        <WorkflowText>
          Steeringルールとは「AIにどう振る舞ってほしいか」を定義するファイルです。これがしっかり書けていると、AIの出力品質が格段に上がります。逆に書かないと、AIは毎回ゼロから推測するため出力がブレます。
        </WorkflowText>

        <WorkflowInfoBox type="preview">
          <Typography variant="body2" fontWeight={600}>
            次の記事: 要件定義編 — Steeringルールの書き方
          </Typography>
          <WorkflowList
            items={[
              'Steeringファイルとは何か',
              'テンプレートと書くべき情報',
              'ベストプラクティス / アンチパターン',
            ]}
          />
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* チェックリスト */}
      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              'Spring Initializrでプロジェクトを生成した',
              'Kiroでdocker-compose.ymlとHELPME.mdを生成した',
              'HELPME.mdの手順に従いMySQLが起動する',
              'application.ymlにDB接続設定を追記した',
              'Spring Securityの401問題をKiroで解決した',
              './gradlew bootRun でアプリが起動し、curlで404が返る',
              'GitHubにリポジトリを作成し、プッシュ完了した',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonStoryScaffold;
